<template>
  <v-card class="card-pad">
    <v-toolbar flat class="back">
      <v-btn small plain color="grey" @click="$router.back()">
        <v-icon x-small class="mr-1">mdi-chevron-left</v-icon>
        {{ $t('Voltar') }}
      </v-btn>
    </v-toolbar>

    <v-toolbar flat class="mb-5 back">
      <div class="d-flex align-end">
        <div id="iconTitle">
          <v-icon id="icon" size="30px" color="white">
            mdi-archive-check-outline
          </v-icon>
        </div>

        <v-toolbar-title>{{ $t('Cadastrar item') }}</v-toolbar-title>
      </div>
    </v-toolbar>

    <v-card class="card pa-2 mx-4 my-8">
      <v-form ref="form">
        <v-row class="ma-0 mt-4" dense>
          <v-col cols="12" sm="3">
            <span class="text ml-1">{{ $t('Nome') }}</span>
            <v-text-field
              v-model="item.name"
              :rules="[(v) => !!v || 'Nome obrigatório']"
              class="primary-input"
              :placeholder="$t('Insira um nome para o item')"
              outlined
              hide-details="auto"
            >
              <template #message="{ message }">
                {{ $t(message) }}
              </template>
            </v-text-field>
          </v-col>

          <v-col cols="12" sm="3">
            <span class="text ml-1">{{ $t('Instituição') }}</span>
            <v-autocomplete
              :disabled="isEditing || institution.userType > 1"
              v-model="institution"
              :items="institutions"
              :loading="instLoading"
              item-text="name"
              :rules="[(v) => !!v || 'Instituição obrigatória']"
              return-object
              append-icon="mdi-chevron-down"
              class="primary-input"
              :placeholder="$t('Pesquisar por nome')"
              outlined
              @change="getUnidades"
              hide-details="auto"
            >
              <template #message="{ message }">
                {{ $t(message) }}
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="3">
            <span class="text ml-1">{{ $t('Unidade') }}</span>
            <v-autocomplete
              multiple
              :loading="unidLoading"
              :items="unidades"
              :rules="[(v) => !!v || 'Unidade obrigatória']"
              return-object
              item-value="id"
              item-text="name"
              v-model="selectedUnits"
              append-icon="mdi-chevron-down"
              class="primary-input"
              :placeholder="$t('Pesquisar por nome')"
              outlined
              hide-details="auto"
            >
              <template v-slot:prepend-item>
                <v-list-item ripple @mousedown.prevent @click="toggle">
                  <v-list-item-action>
                    <v-icon
                      :color="selectedUnits.length > 0 ? 'indigo darken-4' : ''"
                    >
                      {{ icon }}
                    </v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $t('Todos') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="mt-2"></v-divider>
              </template>

              <template #message="{ message }">
                {{ $t(message) }}
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="3">
            <span class="text ml-1">{{ $t('Tipo de saída') }}</span>
            <v-select
              :items="exitTypes"
              item-text="text"
              :disabled="isEditing"
              item-value="value"
              :rules="[(v) => v != null || 'Tipo de saída obrigatória']"
              v-model="item.exitType"
              append-icon="mdi-chevron-down"
              class="primary-input"
              :placeholder="$t('Selecione o tipo de saída')"
              outlined
              hide-details="auto"
            >
              <template v-slot:selection="{ item }">
                <span class="text-caption">
                  {{ $t(item.text) }}
                </span>
              </template>

              <template v-slot:item="{ item }">
                <span class="text-caption">
                  {{ $t(item.text) }}
                </span>
              </template>

              <template #message="{ message }">
                {{ $t(message) }}
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" sm="3" v-if="isEditing">
            <span class="text ml-1">{{ $t('Quantidade mínima') }}</span>
            <v-currency-field
              v-model.number="item.minimumQuantity"
              type="number"
              class="primary-input"
              placeholder="0000"
              :decimalLength="3"
              outlined
              hide-details="auto"
            >
            </v-currency-field>
          </v-col>

          <v-col cols="12" sm="3" v-if="isEditing">
            <span class="text ml-1">{{ $t('Quantidade em estoque') }}</span>
            <v-text-field
              v-model="item.totalQuantity"
              readonly
              type="number"
              class="primary-input"
              placeholder="0000"
              outlined
              hide-details="auto"
            >
            </v-text-field>
          </v-col>

          <v-col cols="12" sm="3">
            <span class="text ml-1">Unidade de medida</span>
            <v-select
              v-model.number="item.unit"
              class="primary-input"
              placeholder="Selecione"
              :items="units"
              append-icon="mdi-chevron-down"
              outlined
              hide-details="auto"
            >
              <template v-slot:selection="{ item }">
                <span style="text-transform: capitalize">
                  {{ item }}
                </span>
              </template>

              <template v-slot:item="{ item }">
                <span style="text-transform: capitalize">
                  {{ item }}
                </span>
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" sm="3">
            <span class="text ml-1">{{ $t('Código') }}</span>
            <v-text-field
              v-model="item.code"
              class="primary-input"
              maxlength="13"
              :placeholder="$t('Insira um código para o item')"
              outlined
              hide-details="auto"
            >
            </v-text-field>
          </v-col>

          <v-col cols="12" sm="3" v-if="isEditing">
            <span class="text ml-1">{{ $t('Valor total em estoque') }}</span>
            <v-currency-field
              v-model="item.totalValue"
              readonly
              class="primary-input"
              placeholder="0000"
              outlined
              hide-details="auto"
            >
            </v-currency-field>
          </v-col>
        </v-row>
      </v-form>

      <div class="d-flex justify-end">
        <v-btn
          @click="submit"
          :loading="loading"
          class="primary-button"
          depressed
        >
          {{ isEditing ? $t('Atualizar') : $t('Cadastrar') }}
        </v-btn>
      </div>
    </v-card>

    <v-expand-transition>
      <div v-show="isEditing">
        <v-tabs color="secondary" centered grow v-model="tab">
          <v-tab active-class="activated-tab" class="rounded-lg rounded-b0">
            Entradas
          </v-tab>
          <v-tab active-class="activated-tab" class="rounded-lg rounded-b0">
            {{ $t('Saídas') }}
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <v-tab-item>
            <Entries
              v-if="!loading"
              :stockItemId="item.id"
              :stockItemUnit="item.unit"
              :institutionId="item.institutionId"
              @update="getItem"
            />
          </v-tab-item>
          <v-tab-item>
            <Exits
              v-if="!loading"
              :stockItemId="item.id"
              :stockItemUnit="item.unit"
              :institutionId="item.institutionId"
              @update="getItem"
            />
          </v-tab-item>
        </v-tabs-items>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
import ApiService from '../../../services/ApiService'
import Entries from './components/StockEntries.vue'
import Exits from './components/StockExits.vue'

export default {
  name: 'StockManage',

  data() {
    return {
      units: ['Metro', 'Metro Quadrado', 'Kg', 'Litro', 'Unidade'],
      selectedUnits: [],
      tab: 0,
      loading: false,
      isEditing: false,
      unidades: [],
      unidLoading: false,
      institutions: [],
      instLoading: false,
      institutionId: null,
      item: {
        unidades: [],
      },
      user: {},
      institution: {},
      exitTypes: [
        { text: 'FIFO', value: 0 },
        { text: 'Média', value: 1 },
      ],
      apiService: new ApiService(),
    }
  },

  async created() {
    this.getInstitutions()

    this.user = JSON.parse(localStorage.getItem('user'))
    this.institution = JSON.parse(localStorage.getItem('institution'))

    await this.getUnidades()

    if (this.$route.query.id) {
      await this.getItem()
      const unidadeIds = this.item.unidades.map((x) => x.unidadeId)
      this.selectedUnits = this.unidades.filter((x) =>
        unidadeIds.includes(x.id)
      )
      this.isEditing = true
    }
  },

  components: {
    Entries,
    Exits,
  },

  computed: {
    likesAllUnities() {
      return this.selectedUnits.length === this.unidades.length
    },
    likesSomeUnities() {
      return this.selectedUnits.length > 0 && !this.likesAllUnities
    },

    icon() {
      if (this.likesAllUnities) return 'mdi-close-box'
      if (this.likesSomeUnities) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
  },

  methods: {
    toggle() {
      this.$nextTick(() => {
        if (this.likesAllUnities) {
          this.selectedUnits = []
        } else {
          this.selectedUnits = this.unidades.slice()
        }
      })
    },

    async getItem() {
      this.loading = true
      await this.apiService
        .get(`stockItem/${this.$route.query.id}`)
        .then((response) => {
          this.item = { ...response.content }
          this.item.totalValue = this.item.totalValue.toFixed(2)
          this.institution = { ...this.item.institution }
          this.item.unit = this.item.unit ? this.item.unit : 'Unidade'
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    async getUnidades() {
      this.unidLoading = true

      await this.apiService
        .get(`unidade/listByUserAndInstitution/${this.institution.id}`)
        .then((response) => {
          this.unidades = response.content
          this.unidLoading = false
        })
        .catch(() => {
          this.unidLoading = false
        })
    },

    async getInstitutions() {
      this.instLoading = true

      await this.apiService
        .get(`institution/listUser`)
        .then((response) => {
          this.institutions = response.content
          this.instLoading = false
        })
        .catch(() => {
          this.instLoading = false
        })
    },

    async submit() {
      if (this.$refs.form.validate()) {
        this.loading = true

        this.item.unidades = this.selectedUnits.map((x) => {
          return {
            stockItemId: this.item.id,
            unidadeId: x.id,
          }
        })

        if (this.isEditing) {
          await this.apiService
            .put(`stockItem/edit`, this.item)
            .then(() => {
              this.$toast.success(this.$t('Item atualizado com sucesso!'))
              this.$router.push('/stock')
              this.loading = false
            })
            .catch(() => {
              this.loading = false
            })
        } else {
          await this.apiService
            .post(`stockItem/add`, this.item)
            .then(() => {
              this.$toast.success(this.$t('Item adicionado com sucesso!'))
              this.$router.push('/stock')
              this.loading = false
            })
            .catch(() => {
              this.loading = false
            })
        }
      }
    },
  },

  watch: {
    institution(nv) {
      this.item.institutionId = nv.id
    },
  },
}
</script>

<style scoped>
.card {
  box-shadow: 5px 5px 20px rgba(0, 0, 0, 0.25) !important;
  border-radius: 10px;
}
</style>
